import React from 'react';
import startCase from 'lodash/startCase';
import { ExperimentStatus, VariantType } from 'src/services';
import { Typography, TypographyType, TypographyVariant } from 'src/components-dummy';
import classNames from 'classnames';
import { formatPercent, formatSignedPercent, getDeltaClassName } from 'src/utils';
import { ExperimentCardVariantTableProps } from '../types';
import { PRODUCTION_FIELD_NAME } from '../../ExperimentVariantNameCell';
import { LiftCell } from '../../LiftCell';
import {
  convertDomainGroupChangesToDiffCardProps,
  convertEntityChangesToDomainGroups,
} from 'src/components-bl/FeatureChanges/ChangesPreview/domain-helpers';
import { ParentDomain } from 'src/components-bl/FeatureChanges/ChangesPreview/types';

function formatLift(value: number): JSX.Element {
  const text = formatSignedPercent(value);
  return <span className={getDeltaClassName(value, 'lift-positive', 'lift-negative')}>{text}</span>;
}

export const ExperimentVariantDrawer = ({
  experimentItem,
}: ExperimentCardVariantTableProps): JSX.Element => {
  const { status, variantsDisplayList, analyticsSummary } = experimentItem;

  if (status === ExperimentStatus.Created) {
    return (
      <div className='syte-experiment-variant-drawer'>
        <div className='drawer-title'>
          <Typography type={TypographyType.Body} variant={TypographyVariant.MediumBold}>
            Variant configurations
          </Typography>
        </div>
        <div className='drawer-variant-list'>
          {variantsDisplayList.map(variant => {
            const isProd = variant.type === PRODUCTION_FIELD_NAME;
            const isControl = variant.type === VariantType.Control;
            const variantData = experimentItem.variants.find(v => v.id === variant.id);
            const changes = variantData?.versionChangesSummary;
            const changeCount = changes && !isProd ? changes.totalChanges : 0;

            let diffItems: Array<{ domainTitle: string; cardTitle: string; actionType: string }> =
              [];
            if (!isProd && !isControl) {
              const changedEntities = changes?.changedEntities ?? [];
              if (changedEntities.length > 0) {
                const domainGroups = convertEntityChangesToDomainGroups(changedEntities);
                diffItems = domainGroups.flatMap(domainGroup => {
                  const domainTitle = startCase(
                    (domainGroup as ParentDomain).title ?? domainGroup.domainKey
                  );
                  const cards = convertDomainGroupChangesToDiffCardProps(domainGroup);
                  return cards.map(card => ({
                    domainTitle,
                    cardTitle: String(card.title ?? ''),
                    actionType: String(card.actionType ?? ''),
                  }));
                });
              }
            }

            return (
              <div key={variant.id} className='drawer-variant-row'>
                <div className='drawer-variant-name'>
                  <Typography type={TypographyType.Body} variant={TypographyVariant.MediumMedium}>
                    {isProd ? 'Unallocated 🔒' : variant.name}
                  </Typography>
                  {isControl && <span className='control-badge'>CONTROL</span>}
                </div>
                <div className='drawer-variant-allocation'>
                  <Typography type={TypographyType.Body} variant={TypographyVariant.SmallRegular}>
                    {`${variant.trafficAllocation}%`}
                  </Typography>
                </div>
                <div className='drawer-variant-desc'>
                  {isProd ? (
                    <Typography
                      type={TypographyType.Body}
                      variant={TypographyVariant.SmallRegular}
                      className='drawer-variant-subtitle'
                    >
                      The ongoing shop configuration.
                    </Typography>
                  ) : isControl ? (
                    <Typography
                      type={TypographyType.Body}
                      variant={TypographyVariant.SmallRegular}
                      className='drawer-variant-subtitle'
                    >
                      Baseline — unchanged.
                    </Typography>
                  ) : diffItems.length > 0 ? (
                    diffItems.map((item, idx) => (
                      <Typography
                        key={idx}
                        type={TypographyType.Body}
                        variant={TypographyVariant.SmallRegular}
                        className='drawer-variant-subtitle'
                      >
                        {`${item.domainTitle} › ${item.cardTitle} (${item.actionType})`}
                      </Typography>
                    ))
                  ) : changeCount > 0 ? (
                    <Typography
                      type={TypographyType.Body}
                      variant={TypographyVariant.SmallRegular}
                      className='drawer-variant-subtitle'
                    >
                      {`${changeCount} changes from default`}
                    </Typography>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  const title = status === ExperimentStatus.Running ? 'Variants performance' : 'Final results';

  const variantSummaryByExperienceId = new Map(
    (analyticsSummary?.variantSummary ?? []).map(v => [v.experienceId, v])
  );

  const winningVariant = analyticsSummary?.variantSummary?.find(v => v.isWinner);
  const isCollectingData = analyticsSummary?.state === 'collecting_data';

  return (
    <div className='syte-experiment-variant-drawer'>
      <div className='drawer-title'>
        <Typography type={TypographyType.Body} variant={TypographyVariant.MediumBold}>
          {title}
        </Typography>
      </div>
      <table className='drawer-performance-table'>
        <thead>
          <tr>
            <th>Variant</th>
            <th>Allocation</th>
            <th>Users</th>
            <th>CVR</th>
            <th>Lift (CVR)</th>
          </tr>
        </thead>
        <tbody>
          {variantsDisplayList.map(variant => {
            const isProd = variant.type === PRODUCTION_FIELD_NAME;
            const summary = variantSummaryByExperienceId.get(variant.id);
            const isWinner = winningVariant?.experienceId === variant.id;

            return (
              <tr key={variant.id} className={classNames({ 'winner-row': isWinner })}>
                <td>
                  <div className='drawer-variant-name-cell'>
                    {isProd ? 'Unallocated 🔒' : variant.name}
                    {variant.type === VariantType.Control && (
                      <span className='control-badge'>CONTROL</span>
                    )}
                    {isWinner && (
                      <span className='trophy-icon'>
                        <span aria-hidden='true'>🏆</span>
                        <span className='sr-only'>Winner</span>
                      </span>
                    )}
                  </div>
                </td>
                <td>{`${variant.trafficAllocation}%`}</td>
                <td>{isProd ? '—' : summary ? summary.users.toLocaleString() : '—'}</td>
                <td>
                  {isProd ? 'Not measured.' : summary ? formatPercent(summary.userCvr * 100) : '—'}
                </td>
                <td>
                  <LiftCell
                    canShowLift={
                      !isProd && Boolean(summary) && variant.type !== VariantType.Control
                    }
                    isCollectingData={isCollectingData}
                    liftValue={summary?.lift.cvr ?? 0}
                    formatLift={formatLift}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
